import {
  getLogicPathsForNode,
  getLogicPathsWithoutScreeningNavigation,
} from 'containers/QuestionnaireLogic/utils';
import { getFormattedNodes } from 'containers/QuestionnairePage/selectorUtils';
import { filterAgesUnderEighteen } from 'containers/TargetingPage/Criteria/AgeTargetingCard/helpers';
import {
  SURVEY_TYPE_DISTRIBUTION_LINK,
  SURVEY_TYPE_PEEQ,
  PEEQ_CATEGORY_TYPE_INVOICED,
} from 'containers/Survey/constants';
import { CRON_INTERVAL_MONTHLY } from 'components/SchedulingForm/constants';
import {
  countBy,
  find,
  get,
  isEmpty,
  isNil,
  isNumber,
  isInteger,
} from 'lodash';
import { CONJOINT_ATTRIBUTE_TYPES } from '../QuestionnairePage/QuestionGroup/Conjoint/constants';
import { FIELD_TYPE } from './constants';

export const hasFieldError = ({
  value,
  fieldType,
  valueType,
  validations = {},
  required = false,
  samePageQuestions,
  min,
  max,
}) => {
  let hasNoValue;

  switch (fieldType) {
    case FIELD_TYPE.TEXT:
    case FIELD_TYPE.TEXT_ARRAY:
      hasNoValue = isNil(value) || isEmpty(value);
      break;
    case FIELD_TYPE.TEXT_WITH_MEDIA:
      hasNoValue = isNil(value.text) || isEmpty(value.text);
      break;
    case FIELD_TYPE.TEXT_WITH_MEDIA_REPEATER:
      hasNoValue = isNil(value) || isEmpty(value);
      break;
    case FIELD_TYPE.NUMERIC:
      hasNoValue = isNil(value) || !isNumber(value);
      break;
    case FIELD_TYPE.TEXT_REPEATER:
      hasNoValue = isNil(value) || isEmpty(value);
      break;
    case FIELD_TYPE.CONCEPT_REPEATER:
      hasNoValue = isNil(value) || isEmpty(value);
      break;
    case FIELD_TYPE.ATTRIBUTE_REPEATER:
      hasNoValue = isNil(value) || isEmpty(value);
      break;
    case FIELD_TYPE.BOOLEAN:
      hasNoValue = isNil(value);
      break;
    case FIELD_TYPE.BOOLEAN_TOGGLE:
      hasNoValue = isNil(value);
      break;
    case FIELD_TYPE.NUMBER:
      hasNoValue = isNil(value);
      break;
    default:
      hasNoValue = false;
  }

  if (!required && hasNoValue) {
    return false;
  }

  if (required && hasNoValue) {
    return {
      required: true,
    };
  }

  if (
    fieldType === FIELD_TYPE.TEXT_ARRAY ||
    fieldType === FIELD_TYPE.TEXT_WITH_MEDIA_REPEATER ||
    fieldType === FIELD_TYPE.CONCEPT_REPEATER ||
    fieldType === FIELD_TYPE.ATTRIBUTE_REPEATER ||
    fieldType === FIELD_TYPE.TEXT_REPEATER
  ) {
    if (value.length < min || value.length > max) return true;
  }

  if (fieldType === FIELD_TYPE.TEXT_WITH_MEDIA_REPEATER) {
    if (value.some(({ text }) => isNil(text) || isEmpty(text))) {
      return true;
    }
  }

  if (fieldType === FIELD_TYPE.TEXT_REPEATER) {
    if (value.some(({ text }) => isNil(text) || isEmpty(text))) {
      return true;
    }
  }

  if (fieldType === FIELD_TYPE.CONCEPT_REPEATER) {
    if (
      value.some(
        ({ name, text, media }) =>
          isNil(name) ||
          isEmpty(name) ||
          (isNil(media) && (isNil(text) || isEmpty(text))),
      )
    ) {
      return true;
    }
  }
  if (fieldType === FIELD_TYPE.ATTRIBUTE_REPEATER) {
    const isAnyRequiredPropertyEmpty = value.some(
      ({ name, type, values }) =>
        isNil(name) ||
        isEmpty(name) ||
        isNil(type) ||
        isEmpty(type) ||
        isNil(values) ||
        isEmpty(values),
    );
    const attributesByType = countBy(value, 'type');
    const hasMoreThanOnePriceAttributes =
      attributesByType[CONJOINT_ATTRIBUTE_TYPES.PRICE] > 1;
    if (isAnyRequiredPropertyEmpty || hasMoreThanOnePriceAttributes) {
      return true;
    }
  }

  if (fieldType === FIELD_TYPE.NUMBER) {
    const errors = {};
    if (valueType === 'number-int' && !isInteger(value)) {
      errors.notInteger = true;
      return errors;
    }

    if (!isNil(validations.limitMin) && value < validations.limitMin) {
      errors.limitMin = true;
      return errors;
    }
    if (!isNil(validations.limitMax) && value > validations.limitMax) {
      errors.limitMax = true;
      return errors;
    }

    if (!isNil(validations.min)) {
      const relativeMin = find(samePageQuestions, ['name', validations.min]);

      if (relativeMin && relativeMin.value >= value) {
        errors.minMaxRangeInvalidMin = true;
        return errors;
      }
    }

    if (!isNil(validations.max)) {
      const relativeMax = find(samePageQuestions, ['name', validations.max]);

      if (relativeMax && relativeMax.value <= value) {
        errors.minMaxRangeInvalidMax = true;
        return errors;
      }
    }
  }

  return false;
};

export const getDefaultVariableValue = (
  variableQuestion = {},
  hasSchedulingPerk = false,
) => {
  const { min = 1, value: defaultValues = [] } = variableQuestion;
  switch (variableQuestion.fieldType) {
    case FIELD_TYPE.TEXT:
      return variableQuestion.value || '';
    case FIELD_TYPE.TEXT_ARRAY:
      return [];
    case FIELD_TYPE.TEXT_WITH_MEDIA:
      return { text: undefined, media: undefined };
    case FIELD_TYPE.TEXT_WITH_NUMERIC:
      return { text: undefined, numericValue: undefined, media: undefined };
    case FIELD_TYPE.TEXT_WITH_MEDIA_REPEATER: {
      const values = Array(min)
        .fill()
        .map(() => ({ text: undefined, media: undefined }));
      return values;
    }
    case FIELD_TYPE.TEXT_REPEATER: {
      const values = Object.assign(Array(min).fill(), defaultValues).map(
        (value) => ({
          text:
            value?.text ||
            getDefaultVariableValue({
              fieldType: FIELD_TYPE.TEXT,
            }),
        }),
      );
      return values;
    }
    case FIELD_TYPE.SCHEDULER:
      return hasSchedulingPerk ? CRON_INTERVAL_MONTHLY : undefined;
    case FIELD_TYPE.CONCEPT_REPEATER: {
      const values = Array(min)
        .fill()
        .map(() => ({ name: undefined, text: undefined, media: undefined }));
      return values;
    }
    case FIELD_TYPE.ATTRIBUTE_REPEATER: {
      const values = Array(min)
        .fill()
        .map(() =>
          getDefaultVariableValue({ fieldType: FIELD_TYPE.ATTRIBUTE }),
        );
      return values;
    }
    case FIELD_TYPE.ATTRIBUTE: {
      return {
        name: undefined,
        media: undefined,
        type: CONJOINT_ATTRIBUTE_TYPES.FEATURE,
        values: [
          { text: '', media: undefined },
          { text: '', media: undefined },
        ],
      };
    }
    case FIELD_TYPE.BOOLEAN:
    case FIELD_TYPE.BOOLEAN_TOGGLE:
    case FIELD_TYPE.CARD: {
      const preselectedOption = find(variableQuestion.options, 'selected');
      const defaultValue = get(preselectedOption, 'value', false);
      return defaultValue;
    }
    default:
      return undefined;
  }
};

export const getUpdatedNodes = ({ nodes, ql }) => {
  const logicPaths = getLogicPathsWithoutScreeningNavigation(ql);

  return getFormattedNodes(nodes, logicPaths);
};

export function overrideTemplateBasedOnPerk(
  template,
  userHideAgeUnderEighteen,
) {
  return userHideAgeUnderEighteen
    ? {
        ...template,
        regularTargetingTemplate: {
          ...template.regularTargetingTemplate,
          audiences: template.regularTargetingTemplate.audiences.map(
            (audience) => ({
              ...audience,
              features: audience.features.map((feature) => {
                if (feature.key === 'age') {
                  return {
                    ...feature,
                    values: filterAgesUnderEighteen(feature.values),
                  };
                }

                return feature;
              }),
            }),
          ),
          userHideAgeUnderEighteen,
        },
      }
    : template;
}

export function doesSurveyTypeSupportScheduling(surveyType, peeqCategoryType) {
  switch (true) {
    case surveyType === SURVEY_TYPE_DISTRIBUTION_LINK:
    case surveyType === SURVEY_TYPE_PEEQ &&
      peeqCategoryType === PEEQ_CATEGORY_TYPE_INVOICED:
      return false;
    default:
      return true;
  }
}
